// ** Redux
import { createSlice } from "@reduxjs/toolkit";

// ** Helpers
import { processUserData } from "utils/processUserData";

// ** Actions
import { getUser } from "./actions/get-user.action";
import { setUserStatus } from "./actions/set-user-status.action";
import { getUserEmail } from "./actions/get-user-email.action";
import { suspendAccount } from "./actions/suspend-account.action";
import { setUserDataByUserpanel } from "./actions/set-user-data-by-userpanel.action";
import { logout } from "../global-common.slice";

// Globalny Slice Użytkownika zawierający informacje o zalogowanym użytkowniku

// ############################################################

const initialState = {
	loading: false,
	loaded: false,
	success: false,
	error: false,
	id: null,
	acceptedFields: {
		city: false,
		description: false,
		name: false,
		nameCouple: false,
	},
	awaitAccept: false,
	activityToken: null,
	userDataHeaderHash: null,
	activityUrl: null,
	allowLinks: false,
	frozen: false,
	avatar: "",
	avatarMedia: {
		file: undefined,
		type: undefined,
		contentId: undefined,
		width: undefined,
		height: undefined,
	},
	login: "",
	status_vip: 0,
	online: 0,
	profileStatus: null,
	privateData: false,
	city: null,
	region: null,
	country: null,
	searchSex: [],
	searchAge: [],
	searchFor: [],
	sex: null,
	sound_enable: false,
	urlSearch: "",
	relationship: null,
	languages: [],
	cityName: "",
	regionName: "",
	email: "",
	kids: 0,
	partners: {
		birthday: [],
		sex: [],
		name: [],
		height: [180, 180],
		figure: [],
		zodiak: [],
		hair: [],
		alko: [],
		smoke: [],
		education: [],
	},
	created: null,
	preferences: [],
	tastes: [],
	description: {
		parsed: [],
		plain: "",
	},
	post_left: {
		date: null,
		limit: 0,
		max: 0,
	},
	vip_left: {
		date: null,
		time: null,
	},
	setUserDataByUserpanel: {
		loading: false,
		success: false,
	},

	complete: {},

	suspension: {
		duration: 0,
		to: -1,
		code: -1,
	},
	statistics: {
		galleries: {
			public: 0,
			private: 0,
		},
		likes: 0,
		friends: 0,
		followers: 0,
		user_follow: 0,
		notifications: 0,
		messages: 0,
	},
};

// ############################################################

const globalUserSlice = createSlice({
	name: "global/user",
	initialState,
	reducers: {
		updateProfileDetails: (state, action) => {
			state.frozen = action.payload.frozen;
			state.avatar = action.payload.avatar;
			state.privateData = action.payload.private_data;
			state.status_vip = action.payload.status_vip;
			state.searchSex = action.payload?.search_sex
				? Object.keys(action.payload.search_sex).filter(
						(item) => action.payload.search_sex[item]
				  )
				: [];
			state.awaitAccept = action.payload?.status?.await_accept ?? false;
			state.activityToken = action.payload?.activity_token;
			state.userDataHeaderHash = action.payload.userDataHeaderHash;
		},
		// setUserCountryFromIp: (state, action) => {
		// 	state.userCountryFromIp = action.countryId;
		// },
		setUserOnline: (state, action) => {
			state.online = action.online;
		},
		setUserPartnersHeight: (state, action) => {
			state.partners.height = action.payload;
		},
		setUserSuspension: (state, action) => {
			state.suspension = {
				duration:
					action?.payload?.secondLeft ||
					Math.floor(action.payload.to - Date.now() / 1000),
				to: action.payload.to,
				code: action.payload.code,
				id: action.payload.id,
				isBanForNewContent: action.payload.isBanForNewContent || false,
			};
		},
		clearUserSuspension: (state) => {
			state.suspension = {
				duration: 0,
				to: -1,
				code: -1,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			// ** Get User
			.addCase(getUser.pending, (state, action) => {
				const setUser = action.meta.arg?.setUser || false;
				state.loading = !setUser;
				state.loaded = false;
				state.success = false;
				state.setUserDataByUserpanel.loading = Boolean(setUser);
			})
			.addCase(getUser.fulfilled, (state, action) => {
				state.loading = false;
				state.loaded = true;
				state.success = true;
				state.urlSearch = action.payload.result.url_search;

				const processedData = processUserData(action.payload);
				Object.assign(state, processedData);

				state.setUserDataByUserpanel.loading = false;
				state.setUserDataByUserpanel.success = true;

				state.statistics.galleries.public =
					action.payload.result.statistics?.galleries?.public || 0;
				state.statistics.galleries.private =
					action.payload.result.statistics?.galleries?.private || 0;

				// Uwaga: statystyki self są w innym slice, więc nie możemy ich tutaj obsłużyć
				// Należałoby to zrobić w osobnym slice lub funkcji
			})
			.addCase(getUser.rejected, (state) => {
				state.loading = false;
				state.loaded = true;
				state.success = false;
				state.error = true;
			})
			// ** Suspend Account
			.addCase(suspendAccount.pending, (state) => {
				state.loading = true;
			})
			.addCase(suspendAccount.fulfilled, (state) => {
				state.loading = false;
				state.frozen = !state.frozen;
			})
			.addCase(suspendAccount.rejected, (state) => {
				state.loading = false;
				state.error = true;
			})
			// ** Set User Status
			.addCase(setUserStatus.pending, (state) => {
				state.loading = true;
			})
			.addCase(setUserStatus.fulfilled, (state, action) => {
				state.loading = false;
				state.profileStatus = action.payload.status;
			})
			.addCase(setUserStatus.rejected, (state, action) => {
				state.loading = false;
				state.error =
					action.payload ||
					"Wystąpił błąd podczas ustawiania statusu użytkownika";
			})
			// ** Get Email
			.addCase(getUserEmail.pending, (state) => {
				state.loading = true;
			})
			.addCase(getUserEmail.fulfilled, (state, action) => {
				state.loading = false;
				state.email = action.payload.email;
			})
			.addCase(getUserEmail.rejected, (state) => {
				state.loading = false;
				state.error = true;
			})
			// ** Set UserData by Userpanel
			.addCase(setUserDataByUserpanel.pending, (state) => {
				state.setUserDataByUserpanel.loading = true;
			})
			.addCase(setUserDataByUserpanel.fulfilled, (state) => {
				state.setUserDataByUserpanel.loading = false;
				state.setUserDataByUserpanel.success = true;
			})
			.addCase(setUserDataByUserpanel.rejected, (state) => {
				state.setUserDataByUserpanel.loading = false;
				state.setUserDataByUserpanel.success = false;
			})
			// ** Clear User Data after Logout
			.addCase(logout, () => {
				return initialState;
			});
	},
});

// ############################################################

export default globalUserSlice.reducer;

// ** Actions
export const {
	setUserOnline,
	updateProfileDetails,
	setUserPartnersHeight,
	setUserSuspension,
	clearUserSuspension,
} = globalUserSlice.actions;

// ############################################################
